"use client";

import NextLink from "next/link";
import { useState, useMemo } from "react";
import {
  Box,
  Collapse,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ReactHtmlParser from "@/src/utils/html-parser";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import AlphabetLetter from "./AlphabetLetters";
import ActionButton from "@/src/components/common/broadsheet/action-button";
import CoverPlate from "@/src/components/common/broadsheet/cover-plate";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import CategoryButton from "@/src/components/main-site/home/journals-section/category-button/category-button";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairline,
  hairlineTop,
  HEADING_SIZE,
  SHEET_GUTTER,
  spineForJournal,
} from "@/src/components/common/broadsheet/tokens";
import {
  overviewEntries,
  overviewText,
} from "@/src/utils/overview-information";
import { stripHTMLTags } from "@/src/utils/strip-html-tags";
import { truncateAtWord } from "@/src/utils/textTransform";
import { VISUALLY_HIDDEN as SR_ONLY } from "@/src/utils/visually-hidden";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const ALL = "#";

const JournalFinder = ({
  mainJournal,
  initialJournals = [],
}: {
  mainJournal: Journal;
  initialJournals?: Journal[];
}) => {
  const journals = initialJournals;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<string>(ALL);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const finderConfig =
    mainJournal?.options?.template_configs?.pages?.journal_finder;

  const categories = useMemo(
    () =>
      Array.from(
        new Set(journals.flatMap((journal) => journal.categories ?? [])),
      ).sort((a, b) => a.localeCompare(b)),
    [journals],
  );

  const availableLetters = useMemo(() => {
    const letters = new Set<string>();
    journals.forEach((journal) => {
      const first = journal.title?.trim()[0]?.toUpperCase();
      if (first && /[A-Z]/.test(first)) letters.add(first);
    });
    return letters;
  }, [journals]);

  const filteredJournals = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return journals.filter((journal) => {
      if (term) {
        const haystack = `${journal.title ?? ""} ${overviewText(
          journal.meta_data,
        )}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (
        selectedCategories.length > 0 &&
        !journal.categories?.some((cat) => selectedCategories.includes(cat))
      ) {
        return false;
      }
      if (
        selectedLetter !== ALL &&
        journal.title?.trim()[0]?.toUpperCase() !== selectedLetter
      ) {
        return false;
      }
      return true;
    });
  }, [journals, searchTerm, selectedCategories, selectedLetter]);

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleCategory = (category: string) =>
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { lg: "flex-end" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Kicker sx={{ mb: 0.25 }}>The complete catalog</Kicker>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1.05,
                letterSpacing: "0.01em",
                color: "custom.ink",
              }}
            >
              Journal Finder
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flex: "none",
            }}
          >
            <SocialIcons direction="column" />
          </Box>
        </Box>
      </Box>

      <Box sx={{ py: { xs: 1, md: 1.25 }, ...hairline }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 24rem" },
            columnGap: 2.5,
            rowGap: 1,
            alignItems: "end",
            "& > *": { minWidth: 0 },
          }}
        >
          <Box>
            {finderConfig?.summary ? (
              <Box
                id="page-summary"
                sx={{
                  fontFamily: FONT_TEXT,
                  fontSize: { xs: "0.9375rem", md: "1rem" },
                  lineHeight: 1.6,
                  color: "custom.inkMuted",
                  maxWidth: "62ch",
                  "& p": { m: 0, mb: 0.375 },
                  "& p:last-of-type": { mb: 0 },
                }}
              >
                {ReactHtmlParser(finderConfig.summary)}
              </Box>
            ) : null}
            {finderConfig?.description ? (
              <Box
                sx={{
                  mt: 0.5,
                  fontFamily: FONT_TEXT,
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  color: "custom.inkMuted",
                  maxWidth: "70ch",
                  "& p": { m: 0, mb: 0.375 },
                  "& p:last-of-type": { mb: 0 },
                }}
              >
                {ReactHtmlParser(finderConfig.description)}
              </Box>
            ) : null}
          </Box>

          <Box component="search">
            <Kicker component="label" htmlFor="journal-finder-search">
              Search the catalog
            </Kicker>
            <TextField
              id="journal-finder-search"
              fullWidth
              size="small"
              placeholder="Title or scope…"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{
                mt: 0.25,
                "& .MuiOutlinedInput-root": {
                  fontFamily: FONT_TEXT,
                  fontSize: "0.9375rem",
                  borderRadius: 0,
                  backgroundColor: "custom.newsprintRaised",
                  "& fieldset": {
                    borderColor: "custom.ruleHairline",
                    borderBottomWidth: "2px",
                    borderBottomColor: "primary.main",
                  },
                  "&:hover fieldset, &.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <MetaLine
              sx={{ mt: 0.375 }}
              items={[
                `Showing ${filteredJournals.length} of ${journals.length}`,
                "sorted A–Z",
              ]}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.25,
          py: 0.75,
          ...hairline,
        }}
      >
        <Kicker component="span" sx={{ mr: 0.375 }}>
          Field
        </Kicker>
        <CategoryButton
          onClick={() => setSelectedCategories([])}
          aria-pressed={selectedCategories.length === 0}
        >
          All
        </CategoryButton>
        {categories.map((category) => (
          <CategoryButton
            key={category}
            onClick={() => toggleCategory(category)}
            aria-pressed={selectedCategories.includes(category)}
          >
            {category}
          </CategoryButton>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.25,
          py: 0.75,
          ...hairline,
        }}
      >
        <Kicker component="span" sx={{ mr: 0.375 }}>
          A–Z
        </Kicker>
        <AlphabetLetter
          isActive={selectedLetter === ALL}
          onClick={() => setSelectedLetter(ALL)}
        >
          All
        </AlphabetLetter>
        {ALPHABET.map((letter) => (
          <AlphabetLetter
            key={letter}
            isDisabled={!availableLetters.has(letter)}
            isActive={selectedLetter === letter}
            onClick={() =>
              availableLetters.has(letter) && setSelectedLetter(letter)
            }
          >
            {letter}
          </AlphabetLetter>
        ))}
      </Box>

      {filteredJournals.length === 0 ? (
        <Typography
          component="p"
          sx={{
            fontFamily: FONT_TEXT,
            fontSize: "1rem",
            color: "custom.inkMuted",
            textAlign: "center",
            py: 3,
          }}
        >
          No journals match those filters.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            columnGap: 2.5,
          }}
        >
          {filteredJournals.map((journal) => {
            const meta = journal.meta_data ?? ({} as Journal["meta_data"]);
            const journalUrl =
              journal.journal_url || `/journals/${journal.path}`;
            const cover = meta?.default_cover_image_thumbnail
              ? `${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${meta.default_cover_image_thumbnail}`
              : null;
            const overview = truncateAtWord(
              stripHTMLTags(meta?.description),
              190,
            );
            const apc =
              meta?.acceptance_fee &&
              !/^more$/i.test(String(meta.acceptance_fee))
                ? meta.acceptance_fee
                : null;
            const entryId = String(journal.id);
            const panelId = `journal-${entryId}-detail`;
            const isOpen = !!expanded[entryId];
            const details: [string, React.ReactNode][] = [
              meta?.nlm_ta ? ["Abbreviation", meta.nlm_ta] : null,
              meta?.issn_ppub ? ["Print ISSN", meta.issn_ppub] : null,
              meta?.publication_time
                ? ["Accept to publication", `${meta.publication_time} days`]
                : null,
              meta?.open_peer_review
                ? ["Open peer-reviewed articles", meta.open_peer_review]
                : null,
              meta?.review_time ? ["Review time", meta.review_time] : null,
              meta?.languages ? ["Language", meta.languages] : null,
              meta?.type_of_publishing
                ? ["Publishing model", meta.type_of_publishing]
                : null,
              ...overviewEntries(meta).map(
                (entry): [string, React.ReactNode] => [
                  entry.key?.trim() || "",
                  <Box
                    key="value"
                    component="span"
                    sx={{ "& p": { m: 0, display: "inline" } }}
                  >
                    {ReactHtmlParser(entry.value as string)}
                  </Box>,
                ],
              ),
            ].filter(Boolean) as [string, React.ReactNode][];

            return (
              <Box
                key={journal.id}
                sx={{ display: "flex", gap: 0.875, py: 1, ...hairlineTop }}
              >
                <CoverPlate
                  src={cover}
                  alt=""
                  width={92}
                  height={124}
                  spine={spineForJournal(journal)}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    component="h2"
                    sx={{
                      m: 0,
                      fontFamily: FONT_TEXT,
                      fontWeight: 700,
                      fontSize: "1.0625rem",
                      lineHeight: 1.25,
                      "& a": {
                        color: "custom.link",
                        textDecoration: "none",
                        "&:hover": {
                          color: "custom.linkHover",
                          textDecoration: "underline",
                          textUnderlineOffset: "0.15em",
                        },
                      },
                    }}
                  >
                    <NextLink href={journalUrl}>{journal.title}</NextLink>
                  </Typography>

                  <MetaLine
                    sx={{ mt: 0.125 }}
                    items={[
                      journal.categories?.[0],
                      meta?.frequency,
                      meta?.issn_epub ? `ISSN ${meta.issn_epub}` : null,
                    ]}
                  />

                  {overview ? (
                    <Typography
                      component="p"
                      sx={{
                        m: 0,
                        mt: 0.375,
                        fontFamily: FONT_TEXT,
                        fontSize: "0.8125rem",
                        lineHeight: 1.55,
                        color: "custom.inkMuted",
                      }}
                    >
                      {overview}
                    </Typography>
                  ) : null}

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "baseline",
                      columnGap: 0.875,
                      rowGap: 0.125,
                      mt: 0.5,
                      fontFamily: FONT_UI,
                      fontSize: "0.71875rem",
                      color: "custom.inkMuted",
                      "& b": { color: "custom.link", fontWeight: 700 },
                    }}
                  >
                    {meta?.citation_score ? (
                      <span>
                        <b>{meta.citation_score}</b> CiteScore
                      </span>
                    ) : null}
                    {meta?.if_score ? (
                      <span>
                        <b>{meta.if_score}</b> JIF
                      </span>
                    ) : null}
                    {meta?.time_to_first_decision ? (
                      <span>
                        <b>{meta.time_to_first_decision}</b> days to decision
                      </span>
                    ) : null}
                    {meta?.acceptance_rate ? (
                      <span>
                        <b>{meta.acceptance_rate}%</b> acceptance
                      </span>
                    ) : null}
                    {apc ? (
                      <span>
                        <b>{apc}</b> APC
                      </span>
                    ) : null}
                    <Box
                      component="button"
                      type="button"
                      onClick={() => toggleExpanded(entryId)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      sx={{
                        ml: "auto",
                        p: 0,
                        border: 0,
                        background: "none",
                        cursor: "pointer",
                        font: "inherit",
                        fontWeight: 700,
                        color: "custom.link",
                        paddingBlock: "0.375rem",
                        "&:hover": { color: "custom.linkHover" },
                      }}
                    >
                      {isOpen ? "Less \u25b4" : "More \u25be"}
                      <Box component="span" sx={SR_ONLY}>
                        {` about ${journal.title}`}
                      </Box>
                    </Box>
                  </Box>

                  <Collapse in={isOpen} unmountOnExit>
                    <Box
                      id={panelId}
                      sx={{
                        mt: 0.625,
                        px: 0.875,
                        py: 0.625,
                        backgroundColor: "custom.newsprintRaised",
                        border: "1px solid",
                        borderColor: "custom.ruleHairline",
                        fontFamily: FONT_UI,
                        fontSize: "0.75rem",
                        lineHeight: 1.7,
                        color: "custom.inkMuted",
                        "& a": { color: "custom.link" },
                        "& strong, & b": { color: "custom.ink" },
                      }}
                    >
                      {details.map(([label, value], index) => (
                        <Box key={`${label}-${index}`}>
                          {label ? (
                            <>
                              <Box
                                component="b"
                                sx={{ color: "custom.ink", fontWeight: 700 }}
                              >
                                {label}:
                              </Box>{" "}
                            </>
                          ) : null}
                          {value}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>

                  <Box sx={{ mt: 0.625, display: "flex", gap: 0.375 }}>
                    <ActionButton
                      href={journal.slug || journalUrl}
                      external={!!journal.slug}
                      size="small"
                    >
                      Submit your manuscript
                      <Box component="span" sx={SR_ONLY}>
                        {` to ${journal.title}`}
                      </Box>
                    </ActionButton>
                    <ActionButton
                      href={journalUrl}
                      variant="outline"
                      size="small"
                    >
                      Visit journal
                      <Box component="span" sx={SR_ONLY}>
                        {` ${journal.title}`}
                      </Box>
                    </ActionButton>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default JournalFinder;
